import React, { useMemo, useRef, useState } from 'react'
import { Alert, Box, Stack, TextField, Typography } from '@mui/material'
import MerchantToolShell, { shellStyles } from './MerchantToolShell'
import { useAuth } from '../../contexts/AuthContext'
import { useLanguage } from '../../contexts/LanguageContext'
import { createMerchantEvent, getMerchantEvents } from '../../utils/merchantToolStorage'

const accessTypeOptions = [
    { value: 'general', labelEn: 'General Admission', labelFr: 'Admission generale' },
    { value: 'seat', labelEn: 'Assigned Seat', labelFr: 'Places attribuees' },
    { value: 'table', labelEn: 'Table Booking', labelFr: 'Reservation table' },
]

export default function MerchantCreateEvent() {
    const { user } = useAuth()
    const { lang } = useLanguage()
    const isFr = lang === 'fr'
    const defaultCountry = user?.business_country || user?.businessCountry || user?.country || ''
    const bannerInputRef = useRef(null)
    const [title, setTitle] = useState('')
    const [description, setDescription] = useState('')
    const [city, setCity] = useState('')
    const [address, setAddress] = useState('')
    const [country, setCountry] = useState(defaultCountry)
    const [eventDate, setEventDate] = useState('')
    const [eventTime, setEventTime] = useState('')
    const [accessType, setAccessType] = useState('general')
    const [price, setPrice] = useState('')
    const [ticketCount, setTicketCount] = useState('')
    const [bannerName, setBannerName] = useState('')
    const [bannerPreview, setBannerPreview] = useState('')
    const [createdEvent, setCreatedEvent] = useState(null)
    const [error, setError] = useState('')

    const existingEvents = useMemo(() => getMerchantEvents().slice(0, 3), [])

    const accessTypeHelper = useMemo(() => {
        if (accessType === 'seat') {
            return isFr
                ? 'Les clients choisissent des sieges ou des sections definies.'
                : 'Guests buy seat-based tickets with sections or assigned rows.'
        }
        if (accessType === 'table') {
            return isFr
                ? 'Les clients reservent des tables ou des espaces groupe.'
                : 'Guests book tables or grouped hospitality access.'
        }
        return isFr
            ? 'Les clients achetent des billets standards sans siege ni table.'
            : 'Guests buy standard tickets with no fixed seat or table assignment.'
    }, [accessType, isFr])

    const handleBannerPick = (event) => {
        const file = event.target.files?.[0]
        if (!file) return
        setBannerName(file.name)
        try {
            setBannerPreview(URL.createObjectURL(file))
        } catch {
            setBannerPreview('')
        }
    }

    const openBannerPicker = () => {
        bannerInputRef.current?.click()
    }

    const handleCreate = () => {
        const trimmedTitle = title.trim()
        const trimmedCity = city.trim()
        const trimmedCountry = country.trim()
        const numericPrice = Number(price || 0)
        const numericTicketCount = Number(ticketCount || 0)

        if (!trimmedTitle) {
            setError(isFr ? 'Ajoutez un titre evenement.' : 'Add an event title.')
            return
        }
        if (!trimmedCity) {
            setError(isFr ? 'Ajoutez le lieu ou la ville.' : 'Add the venue or city.')
            return
        }
        if (!trimmedCountry) {
            setError(isFr ? 'Ajoutez le pays.' : 'Add the country.')
            return
        }
        if (!eventDate || !eventTime) {
            setError(isFr ? 'Ajoutez la date et l heure.' : 'Add the date and time.')
            return
        }
        if (!(numericPrice > 0)) {
            setError(isFr ? 'Ajoutez un prix valide.' : 'Add a valid price.')
            return
        }
        if (!(numericTicketCount > 0)) {
            setError(isFr ? 'Ajoutez le total billets.' : 'Add total tickets.')
            return
        }

        const record = createMerchantEvent({
            title: trimmedTitle,
            description: description.trim(),
            city: trimmedCity,
            address: address.trim(),
            country: trimmedCountry,
            eventDate,
            eventTime,
            accessType,
            price: numericPrice,
            ticketCount: numericTicketCount,
            ticketsSold: 0,
            bannerName,
            bannerPreview,
        })

        setCreatedEvent(record)
        setError('')
    }

    return (
        <MerchantToolShell
            eyebrow={isFr ? 'Billetterie' : 'Ticketing'}
            title={isFr ? 'Creer evenement' : 'Create Event'}
            subtitle={
                isFr
                    ? 'Utilisez le meme flux clair que sur mobile pour lancer votre evenement, ajouter le lieu, le type de billet et la capacite.'
                    : 'Use the same clean flow as mobile to launch your event, add the venue, ticket type, and selling capacity.'
            }
            chips={[
                'General',
                isFr ? 'VIP ou table' : 'VIP or table',
                isFr ? 'Pret a vendre' : 'Ready to sell',
            ]}
            primaryAction={{
                label: isFr ? 'Creer evenement' : 'Create Event',
                onClick: handleCreate,
            }}
            left={
                <Stack spacing={2.2}>
                    {error && <Alert severity="error">{error}</Alert>}
                    {createdEvent && (
                        <Alert severity="success">
                            {isFr
                                ? `Evenement cree: ${createdEvent.title}. Reference ${createdEvent.id.toUpperCase()}.`
                                : `Event created: ${createdEvent.title}. Reference ${createdEvent.id.toUpperCase()}.`}
                        </Alert>
                    )}

                    <Box>
                        <Typography sx={{ fontWeight: 800, mb: 0.6 }}>
                            {isFr ? 'Banniere evenement' : 'Event Banner'}
                        </Typography>
                        <Typography sx={{ color: 'text.secondary', fontSize: 13, mb: 1.5 }}>
                            {isFr
                                ? 'Taille recommandee: 1600 x 700 px'
                                : 'Recommended size: 1600 x 700 px'}
                        </Typography>
                        <input
                            ref={bannerInputRef}
                            type="file"
                            accept="image/*"
                            hidden
                            onChange={handleBannerPick}
                        />
                        <Box
                            onClick={openBannerPicker}
                            role="button"
                            tabIndex={0}
                            onKeyDown={(event) => {
                                if (event.key === 'Enter' || event.key === ' ') {
                                    event.preventDefault()
                                    openBannerPicker()
                                }
                            }}
                            sx={{
                                ...shellStyles.softPanel,
                                minHeight: 180,
                                display: 'grid',
                                placeItems: 'center',
                                textAlign: 'center',
                                overflow: 'hidden',
                                cursor: 'pointer',
                                backgroundImage: bannerPreview ? `linear-gradient(rgba(8,6,27,0.12), rgba(8,6,27,0.12)), url(${bannerPreview})` : undefined,
                                backgroundSize: 'cover',
                                backgroundPosition: 'center',
                                '&:hover': {
                                    borderColor: 'rgba(255,138,0,0.5)',
                                },
                            }}
                        >
                            <Stack spacing={1} sx={{ px: 2 }}>
                                <Typography sx={{ fontWeight: 800, color: bannerPreview ? '#FFFFFF' : 'inherit' }}>
                                    {bannerPreview
                                        ? (isFr ? 'Cliquer pour changer la banniere' : 'Click to change banner')
                                        : (isFr ? 'Touchez pour ajouter la banniere' : 'Tap to add event banner')}
                                </Typography>
                                <Typography sx={{ color: bannerPreview ? 'rgba(255,255,255,0.88)' : 'text.secondary', fontSize: 13 }}>
                                    {bannerName
                                        ? bannerName
                                        : isFr
                                            ? 'Utilisez une image large, idealement 1600 x 700 px.'
                                            : 'Use a wide cover image, ideally 1600 x 700 px.'}
                                </Typography>
                            </Stack>
                        </Box>
                    </Box>

                    <Typography sx={{ fontWeight: 800, pt: 0.5 }}>
                        {isFr ? 'Infos evenement' : 'Event Info'}
                    </Typography>
                    <TextField
                        label={isFr ? 'Titre evenement' : 'Event Title'}
                        value={title}
                        onChange={(e) => setTitle(e.target.value)}
                    />
                    <TextField
                        label={isFr ? 'Description' : 'Description'}
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}
                        multiline
                        minRows={3}
                    />

                    <Typography sx={{ fontWeight: 800, pt: 0.5 }}>
                        {isFr ? 'Lieu' : 'Location'}
                    </Typography>
                    <TextField
                        label={isFr ? 'Lieu / ville' : 'Venue / City'}
                        value={city}
                        onChange={(e) => setCity(e.target.value)}
                    />
                    <TextField
                        label={isFr ? 'Adresse' : 'Address'}
                        value={address}
                        onChange={(e) => setAddress(e.target.value)}
                    />
                    <TextField
                        label={isFr ? 'Pays' : 'Country'}
                        value={country}
                        onChange={(e) => setCountry(e.target.value)}
                    />

                    <Typography sx={{ fontWeight: 800, pt: 0.5 }}>
                        {isFr ? 'Date et heure' : 'Date & Time'}
                    </Typography>
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                        <TextField
                            type="date"
                            label={isFr ? 'Date' : 'Date'}
                            value={eventDate}
                            onChange={(e) => setEventDate(e.target.value)}
                            InputLabelProps={{ shrink: true }}
                            fullWidth
                        />
                        <TextField
                            type="time"
                            label={isFr ? 'Heure' : 'Time'}
                            value={eventTime}
                            onChange={(e) => setEventTime(e.target.value)}
                            InputLabelProps={{ shrink: true }}
                            fullWidth
                        />
                    </Stack>

                    <Typography sx={{ fontWeight: 800, pt: 0.5 }}>
                        {isFr ? 'Billets' : 'Tickets'}
                    </Typography>
                    <TextField
                        label={isFr ? 'Type acces' : 'Access Type'}
                        value={accessType}
                        onChange={(e) => setAccessType(e.target.value)}
                        select
                        SelectProps={{ native: true }}
                    >
                        {accessTypeOptions.map((option) => (
                            <option key={option.value} value={option.value}>
                                {isFr ? option.labelFr : option.labelEn}
                            </option>
                        ))}
                    </TextField>
                    <Box sx={shellStyles.softPanel}>
                        <Typography sx={{ color: 'text.secondary', fontSize: 13 }}>
                            {accessTypeHelper}
                        </Typography>
                    </Box>
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                        <TextField
                            label={isFr ? 'Prix' : 'Price'}
                            value={price}
                            onChange={(e) => setPrice(e.target.value)}
                            fullWidth
                        />
                        <TextField
                            label={isFr ? 'Total billets' : 'Total Tickets'}
                            value={ticketCount}
                            onChange={(e) => setTicketCount(e.target.value)}
                            fullWidth
                        />
                    </Stack>
                </Stack>
            }
            right={
                <Stack spacing={2}>
                    <Box sx={shellStyles.softPanel}>
                        <Typography sx={{ fontWeight: 800, mb: 0.8 }}>
                            {isFr ? 'Avant publication' : 'Before launch'}
                        </Typography>
                        <Typography sx={{ color: 'text.secondary', fontSize: 13 }}>
                            {isFr
                                ? 'Le web peut lancer rapidement un evenement. Les details plus riches peuvent ensuite rester sur mobile.'
                                : 'Web can launch the event quickly. The richer setup can still stay on mobile after creation.'}
                        </Typography>
                    </Box>
                    <Box sx={shellStyles.softPanel}>
                        <Typography sx={{ fontWeight: 800, mb: 0.8 }}>
                            {isFr ? 'Billets et sections' : 'Tickets and sections'}
                        </Typography>
                        <Typography sx={{ color: 'text.secondary', fontSize: 13 }}>
                            {isFr
                                ? 'General, VIP, VVIP, Couple, siege ou table doivent rester alignes avec le flux mobile.'
                                : 'General, VIP, VVIP, Couple, seat, or table setup should stay aligned with the mobile ticket flow.'}
                        </Typography>
                    </Box>
                    <Box sx={shellStyles.softPanel}>
                        <Typography sx={{ fontWeight: 800, mb: 0.8 }}>
                            {isFr ? 'Promotion' : 'Promotion'}
                        </Typography>
                        <Typography sx={{ color: 'text.secondary', fontSize: 13 }}>
                            {isFr
                                ? 'Une fois cree, l evenement doit se partager proprement vers diaspora et local.'
                                : 'Once created, the event should share cleanly to both diaspora and local audiences.'}
                        </Typography>
                    </Box>
                </Stack>
            }
            bottom={
                <Stack spacing={2}>
                    <Typography sx={{ fontWeight: 800 }}>
                        {isFr ? 'Evenements recents' : 'Recent Events'}
                    </Typography>
                    {createdEvent ? (
                        <Stack sx={shellStyles.softPanel} spacing={1}>
                            <Typography sx={{ fontWeight: 800 }}>{createdEvent.title}</Typography>
                            <Typography sx={{ color: 'text.secondary', fontSize: 13 }}>
                                {isFr
                                    ? `${createdEvent.city} | ${createdEvent.accessType} | Prix ${createdEvent.price} | Billets ${createdEvent.ticketCount}`
                                    : `${createdEvent.city} | ${createdEvent.accessType} | Price ${createdEvent.price} | Tickets ${createdEvent.ticketCount}`}
                            </Typography>
                        </Stack>
                    ) : existingEvents.length > 0 ? (
                        existingEvents.map((event) => (
                            <Stack key={event.id} sx={shellStyles.softPanel} spacing={0.8}>
                                <Typography sx={{ fontWeight: 800 }}>{event.title}</Typography>
                                <Typography sx={{ color: 'text.secondary', fontSize: 13 }}>
                                    {isFr
                                        ? `${event.city} | ${event.accessType} | Prix ${event.price}`
                                        : `${event.city} | ${event.accessType} | Price ${event.price}`}
                                </Typography>
                            </Stack>
                        ))
                    ) : (
                        <Typography sx={{ color: 'text.secondary' }}>
                            {isFr
                                ? 'Aucun evenement web cree pour le moment.'
                                : 'No web event created yet.'}
                        </Typography>
                    )}
                </Stack>
            }
        />
    )
}
